/**
 * "Sample Color" — an in-app eyedropper that works in every browser.
 *
 * A colour picker calls `startSampling({ onPick })`; the canvas shows a crosshair overlay over the page (see
 * components/editor/SampleColorOverlay.js), renders the page once to an offscreen canvas and reads the pixel under the cursor
 * — pictures, gradients, text, shapes, anything on the page. Click picks the colour, Escape cancels.
 */
let sampler = { active: false, onPick: null, label: 'Color' };
const listeners = new Set();

const emit = () => listeners.forEach((fn) => { try { fn(sampler); } catch { /* ignore */ } });

export const getSampler = () => sampler;
export const subscribeSampler = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

/** Begin sampling; `onPick(hex)` receives the picked colour. */
export const startSampling = ({ onPick, label = 'Color' }) => {
  sampler = { active: true, onPick, label };
  emit();
};

/** End sampling (cancel or after a pick). */
export const stopSampling = () => {
  if (!sampler.active) return;
  sampler = { active: false, onPick: null, label: 'Color' };
  emit();
};

/** Finish with a colour. */
export const finishSampling = (hex) => {
  const cb = sampler.onPick;
  stopSampling();
  if (cb && hex) cb(hex);
};

/** RGBA pixel (0-255, alpha 0-255) → '#rrggbb', compositing over `bg` when the pixel is (semi-)transparent. */
export const pixelToHex = (r, g, b, a = 255, bg = '#ffffff') => {
  const alpha = a / 255;
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(bg || '#ffffff');
  const br = m ? parseInt(m[1], 16) : 255, bgc = m ? parseInt(m[2], 16) : 255, bb = m ? parseInt(m[3], 16) : 255;
  const mix = (c, back) => Math.round(c * alpha + back * (1 - alpha));
  const h = (v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
  return `#${h(mix(r, br))}${h(mix(g, bgc))}${h(mix(b, bb))}`;
};
